import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  styled
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { FiUpload } from 'react-icons/fi';
import { PendingAttachment } from '../../../../types/receipts';
import { BasePaper } from './styles/SharedStyles';

const UploaderPaper = styled(Paper)(({ theme }) => ({
  background: 'rgb(26, 31, 46)',
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(3),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #0EA5E9 0%, #10B981 100%)'
  }
}));

const DropZoneBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'isDragActive'
})<{ isDragActive: boolean }>(({ theme, isDragActive }) => ({
  border: '2px dashed',
  borderColor: isDragActive ? '#0EA5E9' : 'rgba(255, 255, 255, 0.12)',
  borderRadius: '12px',
  padding: theme.spacing(6),
  textAlign: 'center',
  cursor: 'pointer',
  background: isDragActive 
    ? 'linear-gradient(rgba(14, 165, 233, 0.05), rgba(16, 185, 129, 0.05))'
    : 'transparent',
  transition: 'all 0.3s ease-in-out',
  backdropFilter: 'blur(8px)',
  '&:hover': {
    borderColor: '#0EA5E9',
    background: 'linear-gradient(rgba(14, 165, 233, 0.03), rgba(16, 185, 129, 0.03))',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)'
  }
}));

const UploadIcon = styled(FiUpload)(({ theme }) => ({
  width: '32px',
  height: '32px',
  color: '#0EA5E9',
  animation: 'bounce 2s infinite ease-in-out',
  '@keyframes bounce': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-5px)' }
  }
}));

const MainText = styled(Typography)(({ theme }) => ({
  color: '#F1F5F9',
  fontSize: '1.1rem',
  fontWeight: 500,
  marginBottom: theme.spacing(1),
  letterSpacing: '-0.02em'
}));

const SubText = styled(Typography)(({ theme }) => ({
  color: '#94A3B8',
  fontSize: '0.9rem',
  letterSpacing: '0.01em'
}));

interface SmartReceiptUploaderProps {
  onAttachmentsChange: (attachments: PendingAttachment[]) => void;
  maxFiles?: number;
  maxSize?: number;
  allowedTypes?: string[];
}

export const SmartReceiptUploader: React.FC<SmartReceiptUploaderProps> = ({
  onAttachmentsChange,
  maxFiles = 12,
  maxSize = 10485760, // 10MB
  allowedTypes = ['image/*', 'application/pdf']
}) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newAttachments: PendingAttachment[] = acceptedFiles.map(file => ({
      id: crypto.randomUUID(),
      file,
      name: file.name,
      type: file.type,
      size: file.size,
      status: 'pending',
      uploadProgress: 0,
      metadata: {}
    }));

    onAttachmentsChange(newAttachments);
  }, [onAttachmentsChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles,
    maxSize,
    accept: allowedTypes.reduce((acc, type) => ({ ...acc, [type]: [] }), {}),
  });

  return (
    <UploaderPaper>
      <DropZoneBox
        {...getRootProps()}
        isDragActive={isDragActive}
      >
        <input {...getInputProps()} />
        <UploadIcon />
        <MainText>
          {isDragActive
            ? 'Drop files to instantly process...'
            : 'Drag & drop files here, or click to select'}
        </MainText>
        <SubText>
          Supported formats: Images, PDFs (Max {maxFiles} files, {maxSize / 1048576}MB each)
        </SubText>
      </DropZoneBox>
    </UploaderPaper>
  );
};

export default SmartReceiptUploader; 